import { defineStore } from 'pinia'
import _ from 'lodash'
import { authApi, resourceApi } from './common/api'

let _usernameCheckTimer = null
import {
  getAvatar,
  DateTime,
  getAllCountries,
  rules,
  defaultItemsPerPage,
  roleColors
} from './common/commonFunctions'

const inviteApi = resourceApi.extend('invite')
const roleApi = resourceApi.extend('role')
const institutionApi = resourceApi.extend('institution')
const linkApi = resourceApi.extend('userLink')
const registeredApi = resourceApi.extend('userData')
const unregisteredApi = resourceApi.extend('userInvite')
const userRoleApi = resourceApi.extend('userRole')
const userRecordsApi = resourceApi.extend('user')


export const useUser = defineStore('user', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['itemsPerPage'],
      storage: localStorage
    }
  ],

  state() {
    return {
      isLoadingInvites: false,
      errorInvites: null,
      statusInvites: null,
      isLoadingUnregisteredUsers: false,
      errorUnregisteredUsers: null,
      statusUnregisteredUsers: null,
      isLoadingRegisteredUsers: false,
      errorRegisteredUsers: null,
      statusRegisteredUsers: null,
      isLoadingArchivedUsers: false,
      errorArchivedUsers: null,
      statusArchivedUsers: null,
      isLoadingArchivingUsers: false,
      errorArchivingUsers: null,
      statusArchivingUsers: null,
      isLoadingEditingUser: false,
      errorEditingUser: null,
      statusEditingUser: null,
      isLoadingEditingUsers: false,
      errorEditingUsers: null,
      statusEditingUsers: null,
      isLoadingRestoringUsers: false,
      errorRestoringUsers: null,
      statusRestoringUsers: null,
      isLoadingAvailableRoles: false,
      statusAvailableRoles: null,
      invitations: {
        availableRoles: null,
        emails: [],
        verifiedEmails: [],
        roles: null,
        institution: null,
        sent: false,
        sentResults: null,
        errorEmails: null,
        skippedNotice: null,
        placements: [],
        errorRoles: null,
        successTimeout: 3000,
        failureTimeout: 4000,
        skippedTimeout: 6000
      },
      rules: rules.user,
      unregisteredUsers: null,
      registeredUsers: null,
      deactivatedUsers: null,
      archivedUsers: null,
      fetchLinkFrequency: 300000, // 5 minutes
      fetchUnregisteredUsersInterval: null,
      fetchRegisteredUsersInterval: null,
      fetchArchivedUsersInterval: null,
      fetchAvailableRolesInterval: null,
      adminTabs: null,
      adminTabIndex: null,
      adminTableData: null,
      editUser: null,
      selectedUsers: null,
      itemsPerPage: {
        registered: defaultItemsPerPage,
        deactivated: defaultItemsPerPage,
        pending: defaultItemsPerPage,
        archived: defaultItemsPerPage
      },
      isActionMultiple: {},
      roles: null,

      // ── Learner admin state ────────────────────────────────────────────────
      learners: null,
      isLoadingLearners: false,
      isSendingOtp: false,
      otp: null,
      hint: null,
      error: null,

      // ── Username checking (registration) ──────────────────────────────────
      isCheckingUsername: false,
      usernameHint: '',
      usernameError: '',
      usernameSuggestions: [],

      // ── Web learner login flow ─────────────────────────────────────────────
      loginStep: 'username',
      loginUsername: '',
      loginContactHint: '',
      loginLoading: false,
      loginError: null,
      loginResending: false,
      loginPassword: false,
      loginMustChangePassword: false,
      loginChangeToken: null
    }
  },

  getters: {
    isInvitationsSent: ({ invitations }) => {
      return (
        invitations.sentResults?.success?.length ||
        invitations.sentResults?.failure?.length ||
        invitations.sentResults?.skipped?.length
      )
    },
    getInvitationsSuccess: ({ invitations }) => {
      return invitations.sentResults?.success
    },
    getInvitationsFailure: ({ invitations }) => {
      return invitations.sentResults?.failure
    },
    getInvitationsSkipped: ({ invitations }) => {
      return invitations.sentResults?.skipped
    },
    getFetchLinkFrequency: ({ fetchLinkFrequency }) => {
      return fetchLinkFrequency
    },
    getFetchUnregisteredUsersInterval: ({ fetchUnregisteredUsersInterval }) => {
      return fetchUnregisteredUsersInterval
    },
    getFetchRegisteredUsersInterval: ({ fetchRegisteredUsersInterval }) => {
      return fetchRegisteredUsersInterval
    },
    getFetchArchivedUsersInterval: ({ fetchArchivedUsersInterval }) => {
      return fetchArchivedUsersInterval
    },
    getFetchAvailableRolesInterval: ({ fetchAvailableRolesInterval }) => {
      return fetchAvailableRolesInterval
    },
    getAdminTabs: ({ adminTabs }) => {
      return adminTabs
    },
    getterAdminTabs:
      ({
        registeredUsers,
        deactivatedUsers,
        unregisteredUsers,
        archivedUsers,
        itemsPerPage
      }) =>
      (isDeveloper) => {
        const defaultHeaders = [
          {
            title: 'User',
            key: 'user',
            value: (item) => ({
              name: item.name,
              email:
                item.provider === 'learner' ? item.contactHint : item.email,
              avatar: getAvatar(item)
            })
          },
          {
            title: 'Last Active',
            key: 'session',
            value: (item) =>
              item?.userSession?.[0]?.updateTs
                ? DateTime.fromISO(
                    item?.userSession?.[0]?.updateTs
                  ).toOrdinalDateAndTime()
                : null
          },
          {
            title: 'Roles',
            key: 'roles',
            value: (item) =>
              _.map(item?.userLink?.userRole, ({ role }) => role.name)
          },
          {
            title: 'Institution',
            key: 'institution',
            value: (item) => item?.userLink?.institution?.name
          },
          {
            title: 'Country',
            key: 'country',
            value: 'country'
          }
          // Email/mobile verification columns are redundant for registered users — anyone who logs in
          // or signs up has verified email; instructors/admins who access the portal have verified phone.
          // {
          //   title: 'Email Verified',
          //   key: 'emailVerified',
          //   value: 'emailVerified',
          //   align: 'center'
          // },
          // {
          //   title: 'Mobile Verified',
          //   key: 'mobileVerified',
          //   value: 'mobileVerified',
          //   align: 'center'
          // }
        ]
        const filterKeys = ['user', 'roles', 'institution', 'country']
        const customFilters = {
          user: (value, search) => {
            if (!search) return true
            return (
              (value?.name || '')
                .toLowerCase()
                .includes(search.toLowerCase()) ||
              value.email.toLowerCase().includes(search.toLowerCase())
            )
          },
          roles: (value, search) => {
            if (!search) return true
            const needle = search.toLowerCase()
            const list = Array.isArray(value) ? value : [value]
            return list.some((role) => {
              if (role == null) return false
              if (typeof role === 'string') return role.toLowerCase().includes(needle)
              return (
                (role.name || '').toLowerCase().includes(needle) ||
                (role.code || '').toLowerCase().includes(needle)
              )
            })
          },
          institution: (value, search) => {
            if (!search) return true
            const name = typeof value === 'string' ? value : value?.name
            return (name || '').toLowerCase().includes(search.toLowerCase())
          },
          country: (value, search) => {
            if (!search) return true
            return (
              (value?.name || '')
                .toLowerCase()
                .includes(search.toLowerCase()) ||
              (value?.code || '').toLowerCase().includes(search.toLowerCase())
            )
          }
        }
        const customSort = {
          user: (currentUser, nextUser) => {
            const currentEmail = currentUser.email.toLowerCase()
            const nextEmail = nextUser.email.toLowerCase()
            if (currentEmail != nextEmail)
              return currentEmail.localeCompare(nextEmail)
            const currentName = currentUser?.name?.toLowerCase()
            const nextName = nextUser?.name?.toLowerCase()
            if (currentName == null && nextName != null) return -1
            if (currentName != null && nextName == null) return 1
            return currentName
              .toLowerCase()
              .localeCompare(nextName.toLowerCase())
          },
          country: (currentCountry, nextCountry) => {
            const currentName = currentCountry?.name?.toLowerCase()
            const nextName = nextCountry?.name?.toLowerCase()
            if (currentName == null && nextName != null) return -1
            if (currentName != null && nextName == null) return 1
            if (currentName != nextName)
              return currentName.localeCompare(nextName)
            else {
              const currentCode = currentCountry?.code?.toLowerCase()
              const nextCode = nextCountry?.code?.toLowerCase()
              if (currentCode == null && nextCode != null) return -1
              if (currentCode != null && nextCode == null) return 1
              return currentCode.localeCompare(nextCode)
            }
          }
        }
        const tabs = [
          {
            text: 'Registered',
            value: 'registered',
            itemsPerPage: itemsPerPage.registered,
            count: registeredUsers?.length || null,
            headers: [
              ...defaultHeaders,
              ...(isDeveloper
                ? []
                : [
                    {
                      key: 'actions-registered',
                      sortable: false
                    }
                  ])
            ],
            data: registeredUsers?.length
              ? _.map(registeredUsers, (u, i) => {
                  if (!_.has(u, 'country'))
                    u.country = {
                      name: getAllCountries?.[u?.countryCode]?.name,
                      code: u?.countryCode
                    }
                  u.index = i
                  return _.omit(u, 'countryCode')
                })
              : [],
            filterKeys,
            customFilters,
            customSort
          },
          {
            text: 'Deactivated',
            value: 'deactivated',
            itemsPerPage: itemsPerPage.deactivated,
            count: deactivatedUsers?.length || null,
            headers: [
              ...defaultHeaders,
              ...(isDeveloper
                ? []
                : [
                    {
                      key: 'actions-deactivated',
                      sortable: false
                    }
                  ])
            ],
            data: deactivatedUsers?.length
              ? _.map(deactivatedUsers, (u, i) => {
                  if (!_.has(u, 'country'))
                    u.country = {
                      name: getAllCountries?.[u?.countryCode]?.name,
                      code: u?.countryCode
                    }
                  u.index = i
                  return _.omit(u, 'countryCode')
                })
              : [],
            filterKeys,
            customFilters,
            customSort
          },
          {
            text: 'Archived',
            value: 'archived',
            itemsPerPage: itemsPerPage.archived,
            count: archivedUsers?.length || null,
            headers: [
              ...defaultHeaders,
              ...(isDeveloper
                ? []
                : [
                    {
                      key: 'actions-archived',
                      sortable: false
                    }
                  ])
            ],
            data: archivedUsers?.length
              ? _.map(archivedUsers, (u, i) => {
                  if (!_.has(u, 'country'))
                    u.country = {
                      name: getAllCountries?.[u?.countryCode]?.name,
                      code: u?.countryCode
                    }
                  u.index = i
                  return _.omit(u, 'countryCode')
                })
              : [],
            filterKeys,
            customFilters,
            customSort
          },
          {
            text: 'Pending',
            value: 'pending',
            itemsPerPage: itemsPerPage.pending,
            count: unregisteredUsers?.length || null,
            headers: [
              {
                title: 'Email',
                key: 'email',
                value: 'email'
              },
              {
                title: 'Roles',
                key: 'roles',
                value: (item) =>
                  _.map(item?.userLink?.userRole, ({ role }) => role.name)
              },
              {
                title: 'Institution',
                key: 'institution',
                value: (item) => item?.userLink?.institution?.name
              },
              {
                title: 'Date Sent',
                key: 'sent',
                value: (item) =>
                  item?.updateTs
                    ? DateTime.fromISO(item.updateTs).toOrdinalDateAndTime()
                    : null
              },
              {
                title: 'Expires On',
                key: 'expire',
                value: (item) =>
                  item?.expireTs
                    ? DateTime.fromISO(item.expireTs).toOrdinalDateAndTime()
                    : null
              },
              ...(isDeveloper
                ? []
                : [
                    {
                      key: 'actions-pending',
                      sortable: false
                    }
                  ])
            ],
            data: unregisteredUsers?.length
              ? _.map(unregisteredUsers, (u, i) => {
                  u.index = i
                  return u
                })
              : [],
            filterKeys: ['email', 'roles', 'institution']
          }
        ]
        return _.map(
          _.filter(tabs, (t, i) => i == 0 || t.count > 0),
          (t, i) => {
            t.index = i
            return t
          }
        )
      },
    getEditUser: ({ editUser }) => {
      return editUser
    },
    getAdminTableData: ({ adminTableData }) => {
      return adminTableData
    },
    getIsLoadingInvites: ({ isLoadingInvites }) => {
      return isLoadingInvites
    },
    getIsLoadingUnregisteredUsers: ({ isLoadingUnregisteredUsers }) => {
      return isLoadingUnregisteredUsers
    },
    getIsLoadingRegisteredUsers: ({ isLoadingRegisteredUsers }) => {
      return isLoadingRegisteredUsers
    },
    getIsLoadingArchivedUsers: ({ isLoadingArchivedUsers }) => {
      return isLoadingArchivedUsers
    },
    getIsLoadingArchivingUsers: ({ isLoadingArchivingUsers }) => {
      return isLoadingArchivingUsers
    },
    getIsLoadingRestoringUsers: ({ isLoadingRestoringUsers }) => {
      return isLoadingRestoringUsers
    },
    getIsLoadingEditingUser: ({ isLoadingEditingUser }) => {
      return isLoadingEditingUser
    },
    getIsLoadingEditingUsers: ({ isLoadingEditingUsers }) => {
      return isLoadingEditingUsers
    },
    getIsLoadingAvailableRoles: ({ isLoadingAvailableRoles }) => {
      return isLoadingAvailableRoles
    },
    getIsLoadingMessage: ({
      isLoadingArchivingUsers,
      isLoadingRestoringUsers,
      isLoadingEditingUser,
      isLoadingEditingUsers,
      isActionMultiple,
      isLoadingInvites,
      invitations
    }) => {
      if (isLoadingArchivingUsers)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'archivingUsers') &&
          isActionMultiple.archivingUsers == true
          ? 'Archiving users...'
          : 'Archiving user...'
      if (isLoadingRestoringUsers)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'restoringUsers') &&
          isActionMultiple.restoringUsers == true
          ? 'Restoring users...'
          : 'Restoring user...'
      if (isLoadingInvites)
        return _.has(invitations, 'emails') && _.isArrayLike(invitations.emails) && invitations.emails.length > 0
          ? 'Sending invitations...'
          : 'Sending invitation...'
      if (isLoadingEditingUser) return 'Updating user...'
      if (isLoadingEditingUsers) return 'Updating users...'
      return null
    },
    getErrors: ({
      errorInvites,
      errorUnregisteredUsers,
      errorRegisteredUsers,
      errorArchivedUsers,
      errorArchivingUsers,
      errorEditingUser,
      errorEditingUsers,
      errorRestoringUsers
    }) => {
      let returnValue = []
      if (errorInvites)
        returnValue.push({
          text: errorInvites,
          key: 'invites'
        })
      if (errorUnregisteredUsers)
        returnValue.push({
          text: errorUnregisteredUsers,
          key: 'unregisteredUsers'
        })
      if (errorRegisteredUsers)
        returnValue.push({
          text: errorRegisteredUsers,
          key: 'registeredUsers'
        })
      if (errorArchivedUsers)
        returnValue.push({
          text: errorArchivedUsers,
          key: 'archivedUsers'
        })
      if (errorArchivingUsers)
        returnValue.push({
          text: errorArchivingUsers,
          key: 'archivingUsers'
        })
      if (errorEditingUser)
        returnValue.push({
          text: errorEditingUser,
          key: 'editingUser'
        })
      if (errorEditingUsers)
        returnValue.push({
          text: errorEditingUsers,
          key: 'editingUsers'
        })
      if (errorRestoringUsers)
        returnValue.push({
          text: errorRestoringUsers,
          key: 'restoringUsers'
        })
      return returnValue
    },
    getUserRoles: ({ invitations }) => {
      if (
        !_.has(invitations, 'availableRoles') ||
        !_.isArrayLikeObject(invitations.availableRoles) ||
        _.isEmpty(invitations.availableRoles)
      )
        return null
      return _.map(invitations.availableRoles, (r) => {
        r.color = roleColors[r.code]
        return r
      })
    },
    getRoles: ({ roles }) => {
      return roles
    },

    // ── Learner getters ────────────────────────────────────────────────────
    getLearners: ({ learners }) => learners,
    getIsLoadingLearners: ({ isLoadingLearners }) => isLoadingLearners,
    getIsSendingOtp: ({ isSendingOtp }) => isSendingOtp,
    getOtp: ({ otp }) => otp,
    getHint: ({ hint }) => hint,
    getError: ({ error }) => error,
    getIsCheckingUsername: ({ isCheckingUsername }) => isCheckingUsername,
    getUsernameHint: ({ usernameHint }) => usernameHint,
    getUsernameError: ({ usernameError }) => usernameError,
    getUsernameSuggestions: ({ usernameSuggestions }) => usernameSuggestions
  },

  actions: {
    toggleSendingInvitationEmails() {
      this.invitations.sent = !this.invitations.sent
      if (!this.invitations.sent) {
        this.invitations.sentResults = null
      }
    },
    removeInvitationEmail(email) {
      _.pull(this.invitations.emails, email)
    },

    async verifyInvitationEmails(emails) {
      this.invitations.errorEmails = null
      this.invitations.skippedNotice = null
      if (!_.isEmpty(emails) && _.isArrayLikeObject(emails)) {
        const inputEmails = _.map(emails, (e) => e.trim().toLowerCase())
        const allValid = _.every(inputEmails, (e) =>
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(e)
        )
        if (allValid) {
          // Initialize verifiedEmails array if it is undefined or null
          if (!this.invitations.verifiedEmails) {
            this.invitations.verifiedEmails = []
          }

          let newEmails = _.difference(inputEmails, this.invitations.verifiedEmails)
          if (!_.isEmpty(newEmails)) {
            const params = {
              where: { emails: newEmails },
              active: 1
            }
            try {
              const result = await userRecordsApi.get('', { params })
              const { error, user } = result.data.response

              if (error) {
                this.errorActiveSimulations = error.message
              }
              else {
                // Check if existing active user accounts were caught
                if (!_.isEmpty(user) && _.isArrayLikeObject(user)) {
                  const existingActiveEmails = _.map(user, (u) =>
                    String(u.email).toLowerCase()
                  )

                  // Evict the registered users from our processing batch
                  newEmails = _.difference(newEmails, existingActiveEmails)

                  const skippedCount = user.length
                  this.invitations.skippedNotice =
                    skippedCount === 1
                      ? '1 email was skipped because it matches an existing account.'
                      : `${skippedCount} emails were skipped because they match existing accounts.`
                }

                if (!_.isEmpty(newEmails)) {
                  this.invitations.verifiedEmails = _.union(
                    this.invitations.verifiedEmails,
                    newEmails
                  )
                  this.invitations.emails = _.union(
                    this.invitations.emails || [],
                    newEmails
                  )
                }
              }
            } catch (e) {
              this.invitations.errorEmails =
                e?.response?.data?.response?.error?.message ||
                e.message ||
                String(e) ||
                JSON.stringify(e)
            }
          }

          if (_.isEmpty(newEmails)) {
            this.invitations.emails = _.intersection(
              _.uniq([...(this.invitations.emails || []), ...inputEmails]),
              this.invitations.verifiedEmails
            )
          }
        } else
          this.invitations.errorEmails = 'One or more email addresses have been entered incorrectly.'
      }
    },
    async fetchRoles(withLoader = true, currentRole = null) {
      if (this.invitations.errorRoles != null)
        this.invitations.errorRoles = null
      this.statusAvailableRoles = null
      if (withLoader) this.isLoadingAvailableRoles = true
      try {
        const params =
          currentRole != null &&
          _.isPlainObject(currentRole) &&
          !_.isEmpty(currentRole) &&
          _.has(currentRole, 'code') &&
          _.isString(currentRole.code) &&
          currentRole.code.length == 3
            ? { code: currentRole.code }
            : {}
        const result = await roleApi.get('', { params })
        const { error, role } = result.data.response
        this.statusAvailableRoles = result.data.status
        if (error) this.invitations.errorRoles = error.message
        else {
          if (_.isPlainObject(params) && !_.isEmpty(params))
            this.invitations.availableRoles =
              currentRole == null
                ? [...role]
                : role == null || !_.isArrayLikeObject(role) || role.length < 2
                  ? null
                  : _.map([...role], (r) => {
                      r.current = r.code == currentRole.code
                      return r
                    })
          else this.roles = role
        }
      } catch (e) {
        this.invitations.availableRoles = null
        this.invitations.errorRoles =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingAvailableRoles = false
    },
    async sendInvitationEmails() {
      if (this.invitations.sent) this.toggleSendingInvitationEmails()
      if (this.errorInvites != null) this.errorInvites = null
      this.statusInvites = null
      this.isLoadingInvites = true
      const payload = {
        emails: this.invitations.emails,
        roles: this.invitations.roles,
        institution: this.invitations.institution,
        placements: this.invitations.placements || [],
        origin: location.origin
      }
      try {
        const result = await inviteApi.post('', payload)
        const { error, success, failure, skipped } = result.data.response
        this.statusInvites = result.data.status
        if (error) {
          this.errorInvites = error.message
        } else {
          if (success?.length) {
            const successEmails = _.map(success, 'email')
            if (this.unregisteredUsers) {
              _.remove(this.unregisteredUsers, (u) =>
                successEmails.includes(u.email)
              )
            }
            this.unregisteredUsers = [
              ...(this.unregisteredUsers ?? []),
              ...success
            ]
            if (_.findIndex(this.getAdminTabs, { value: 'pending' }) >= 0) {
              this.adminTabIndex = _.findIndex(this.getAdminTabs, {
                value: 'pending'
              })
              this.setAdminTableData()
            }
            if (
              !_.isEmpty(this.archivedUsers) &&
              _.isArrayLikeObject(this.archivedUsers) &&
              this.archivedUsers.length > 0
            )
              _.remove(this.archivedUsers, ({ email }) =>
                successEmails.includes(email)
              )
          }
          this.invitations.sentResults = {
            success,
            failure,
            skipped: skipped || []
          }
          this.toggleSendingInvitationEmails()
        }
      } catch (e) {
        this.errorInvites =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
        this.statusInvites = e?.status
      }
      this.isLoadingInvites = false
      this.invitations.availableRoles = null
      this.invitations.errorEmails = null
      this.invitations.errorRoles = null
    },
    async fetchUnregisteredUsers(withLoader = true) {
      if (this.errorUnregisteredUsers != null)
        this.errorUnregisteredUsers = null
      this.statusUnregisteredUsers = null
      if (withLoader) this.isLoadingUnregisteredUsers = true
      try {
        const result = await unregisteredApi.get('', { params: { active: 1 } })
        const { error, userInvite } = result.data.response
        this.statusUnregisteredUsers = result.data.status
        if (error) this.errorUnregisteredUsers = error.message
        else {
          this.unregisteredUsers = _.filter(
            userInvite,
            (u) =>
              _.isEmpty(u.userLink?.institution) ||
              u.userLink?.institution.deleteTs == null
          )
        }
      } catch (e) {
        this.errorUnregisteredUsers =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingUnregisteredUsers = false
    },
    async fetchRegisteredUsers(withLoader = true) {
      if (this.errorRegisteredUsers != null) this.errorRegisteredUsers = null
      this.statusRegisteredUsers = null
      if (withLoader) this.isLoadingRegisteredUsers = true
      try {
        const result = await registeredApi.get('', { params: { active: 1 } })
        const { error, userData } = result.data.response
        this.statusRegisteredUsers = result.data.status
        if (error) this.errorRegisteredUsers = error.message
        else {
          this.registeredUsers = _.filter(
            userData,
            (u) =>
              _.isEmpty(u.userLink?.institution) ||
              u.userLink?.institution.deleteTs == null
          )
          this.deactivatedUsers = _.filter(
            userData,
            (u) =>
              !_.isEmpty(u.userLink?.institution) &&
              u.userLink?.institution.deleteTs != null
          )
        }
      } catch (e) {
        this.errorRegisteredUsers =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingRegisteredUsers = false
    },
    async fetchArchivedUsers(withLoader = true) {
      if (this.errorArchivedUsers != null) this.errorArchivedUsers = null
      this.statusArchivedUsers = null
      if (withLoader) this.isLoadingArchivedUsers = true
      try {
        const result = await await registeredApi.get('', {
          params: { active: 0 }
        })
        const { error, userData } = result.data.response
        this.statusArchivedUsers = result.data.status
        if (error) this.errorArchivedUsers = error.message
        else {
          this.archivedUsers = userData
        }
      } catch (e) {
        this.errorArchivedUsers =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivedUsers = false
    },
    async archivingUsers(users, isRegistered) {
      this.isActionMultiple.archivingUsers = users?.length > 1
      this.errorArchivingUsers = null
      this.isLoadingArchivingUsers = true
      this.statusArchivingUsers = null
      const select = isRegistered
        ? {
            id: true,
            email: true,
            name: true,
            countryCode: true,
            emailVerified: true,
            mobileVerified: true,
            picture: true,
            deleteTs: true,
            userLink: {
              id: true,
              refererId: true,
              institution: true
            }
          }
        : {
            id: true,
            email: true,
            code: true,
            expireTs: true,
            updateTs: true,
            deleteTs: true,
            userLink: {
              id: true,
              refererId: true,
              institution: true
            }
          }
      let payload = {
        where: {
          id: {
            in: _.map(users, ({ id }) => id)
          }
        },
        data: {
          deleteTs: 'softDelete'
        },
        select
      }
      if (isRegistered)
        payload.subquery = {
          UserSession: {
            orderBy: {
              updateTs: 'desc'
            },
            take: 1,
            relation: 'userSession',
            select: {
              updateTs: true
            }
          }
        }
      try {
        const result = isRegistered
          ? await registeredApi.patch('', payload)
          : await unregisteredApi.patch('', payload)
        let error = null
        let link = isRegistered ? 'userData' : 'userInvite'
        ;({ error, [link]: link } = result.data.response)
        this.statusArchivingUsers = result.data.status
        if (error) this.errorArchivingUsers = error.message
        else {
          if (isRegistered) {
            const usersById = _.keyBy(users, 'id')
            link.forEach(
              (u) => (u.userLink.userRole = usersById[u.id].userLink.userRole)
            )
            this.archivedUsers = [...(this.archivedUsers ?? []), ...link]
          }
          const usersKey = isRegistered
            ? 'registeredUsers'
            : 'unregisteredUsers'
          if (this[usersKey] != null && this[usersKey].length)
            _.map(users, 'index')
              .sort((a, b) => b - a)
              .forEach((i) => this[usersKey].splice(i, 1))
          if (!isRegistered) this.fetchArchivedUsers()
        }
      } catch (e) {
        this.errorArchivingUsers =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivingUsers = false
      delete this.isActionMultiple.archivingUsers
    },
    async restoringUsers(users) {
      this.isActionMultiple.restoringUsers = users?.length > 1
      this.errorRestoringUsers = null
      this.isLoadingRestoringUsers = true
      this.statusRestoringUsers = null
      const payload = {
        where: {
          id: {
            in: _.map(users, ({ id }) => id)
          }
        },
        data: {
          deleteTs: 'restore'
        },
        select: {
          id: true,
          email: true,
          name: true,
          countryCode: true,
          emailVerified: true,
          mobileVerified: true,
          picture: true,
          userLink: {
            id: true,
            refererId: true,
            institution: true
          }
        },
        subquery: {
          UserSession: {
            orderBy: {
              updateTs: 'desc'
            },
            take: 1,
            relation: 'userSession',
            select: {
              updateTs: true
            }
          }
        }
      }
      try {
        const result = await registeredApi.patch('', payload)
        const { error, userData } = result.data.response
        this.statusRestoringUsers = result.data.status
        if (error) this.errorRestoringUsers = error.message
        else {
          const usersById = _.keyBy(users, 'id')
          userData.forEach(
            (u) => (u.userLink.userRole = usersById[u.id].userLink.userRole)
          )
          this.registeredUsers = [...(this.registeredUsers ?? []), ...userData]
          if (this.archivedUsers != null && this.archivedUsers.length)
            _.map(users, 'index')
              .sort((a, b) => b - a)
              .forEach((i) => this.archivedUsers.splice(i, 1))
        }
      } catch (e) {
        this.errorRestoringUsers =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingRestoringUsers = false
      delete this.isActionMultiple.restoringUsers
    },
    isRolesChanged(editRoles, editUser) {
      const editRoleIds = (editRoles || []).map(({ id }) => id)
      const userRoleIds =
        editUser?.userLink?.userRole?.map(({ role }) => role.id) || []
      return (
        editRoleIds.length != userRoleIds.length ||
        editRoleIds.some((id) => !userRoleIds.includes(id))
      )
    },
    async editingUser(user, isRegistered, tabValue = 'registered') {
      let { id, linkId, name, roles, institution } = user
      if (name != null) name = name.replace(/^\s+|\s+$/g, '')
      if (
        name != this.editUser?.name ||
        this.isRolesChanged(roles, this.editUser) ||
        institution?.id != this.editUser?.userLink?.institution?.id
      ) {
        this.errorEditingUser = null
        this.isLoadingEditingUser = true
        this.statusEditingUser = null
        try {
          if (name != this.editUser?.name) {
            const userRole = (function recur(ur) {
              return _.mapValues(ur, (v) =>
                _.isPlainObject(v) ? recur(v) : true
              )
            })(this.editUser.userLink.userRole?.[0])
            let payload = {
              where: { id },
              data: { name }
            }
            payload.select = isRegistered
              ? {
                  id: true,
                  email: true,
                  name: true,
                  countryCode: true,
                  emailVerified: true,
                  mobileVerified: true,
                  picture: true,
                  deleteTs: true,
                  userLink: {
                    id: true,
                    refererId: true,
                    userRole,
                    institution: true
                  },
                  userSession: {
                    subquery: {
                      orderBy: {
                        updateTs: 'desc'
                      },
                      take: 1
                    },
                    updateTs: true
                  }
                }
              : {
                  id: true,
                  email: true,
                  code: true,
                  expireTs: true,
                  updateTs: true,
                  deleteTs: true,
                  userLink: {
                    id: true,
                    refererId: true,
                    userRole,
                    institution: true
                  }
                }
            if (isRegistered)
              payload.subquery = {
                UserSession: {
                  orderBy: {
                    updateTs: 'desc'
                  },
                  take: 1,
                  relation: 'userSession',
                  select: {
                    updateTs: true
                  }
                }
              }
            const result = isRegistered
              ? await registeredApi.patch('', payload)
              : await unregisteredApi.patch('', payload)
            let error = null
            let link = isRegistered ? 'userData' : 'userInvite'
            ;({ error, [link]: link } = result.data.response)
            this.statusEditingUser = result.data.status
            if (error) this.errorEditingUser = error.message
            else {
              if (!isRegistered)
                this.unregisteredUsers[this.editUser.index] = link[0]
              else if (tabValue == 'registered')
                this.registeredUsers[this.editUser.index] = link[0]
              else if (tabValue == 'deactivated')
                this.deactivatedUsers[this.editUser.index] = link[0]
            }
          }
          if (institution?.id != this.editUser?.userLink?.institution?.id) {
            let payload = {
              where: { id: linkId },
              select: {
                id: true,
                userId: true,
                institutionId: true
              },
              data: {}
            }
            if (institution?.id != this.editUser?.userLink?.institution?.id)
              payload.data.institutionId = institution?.id || null
            let result = await linkApi.patch('', payload)
            let { error, userLink } = result.data.response
            this.statusEditingUser = result.data.status
            if (error) this.errorEditingUser = error.message
            else {
              if (!isRegistered) {
                this.unregisteredUsers[
                  this.editUser.index
                ].userLink.institution = institution
              } else {
                const institutionId = institution?.id || null
                if (
                  institution?.id != this.editUser?.userLink?.institution?.id
                ) {
                  if (institutionId != null) {
                    result = await institutionApi.get('', {
                      params: {
                        where: {
                          id: institutionId
                        }
                      }
                    })
                    ;({ error, institution } = result.data.response)
                    this.statusEditingUser = result.data.status
                    if (error) this.errorEditingUser = error.message
                    else institution = institution[0]
                  }
                }
                const isDeactivated =
                  institutionId != null &&
                  institution != null &&
                  !_.isEmpty(institution) &&
                  institution.deleteTs != null
                if (this.errorEditingUser == null) {
                  if (tabValue == 'registered') {
                    this.registeredUsers[
                      this.editUser.index
                    ].userLink.institution = institution
                    if (isDeactivated) {
                      ;[userLink, this.registeredUsers] = _.partition(
                        this.registeredUsers,
                        (ru) =>
                          _.map(userLink, ({ userId }) => userId).includes(
                            ru.id
                          )
                      )
                      this.deactivatedUsers = [
                        ...(this.deactivatedUsers ?? []),
                        ...userLink
                      ]
                    }
                  } else if (tabValue == 'deactivated') {
                    this.deactivatedUsers[
                      this.editUser.index
                    ].userLink.institution = institution
                    if (!isDeactivated) {
                      ;[userLink, this.deactivatedUsers] = _.partition(
                        this.deactivatedUsers,
                        (du) =>
                          _.map(userLink, ({ userId }) => userId).includes(
                            du.id
                          )
                      )
                      this.registeredUsers = [
                        ...(this.registeredUsers ?? []),
                        ...userLink
                      ]
                    }
                  }
                }
              }
            }
          }
          if (this.isRolesChanged(roles, this.editUser)) {
            const userRole = this.editUser?.userLink?.userRole || null
            if (
              userRole != null &&
              !_.isEmpty(userRole) &&
              _.isArrayLikeObject(userRole)
            ) {
              const payload = {
                where: {
                  id: {
                    in: userRole.map(({ id }) => id)
                  }
                },
                data: {
                  deleteTs: 'softDelete'
                }
              }
              const result = await userRoleApi.patch('', payload)
              const { error } = result.data.response
              this.statusEditingUser = result.data.status
              if (error) this.errorEditingUser = error.message
            }
            if (
              this.errorEditingUser == null &&
              roles != null &&
              !_.isEmpty(roles) &&
              _.isArrayLikeObject(roles)
            ) {
              const payload = {
                data: _.map(roles, ({ id: roleId }) => ({ linkId, roleId }))
              }
              const result = await userRoleApi.post('', payload)
              let { error, userRole } = result.data.response
              this.statusEditingUser = result.data.status
              if (error) this.errorEditingUser = error.message
              else {
                const rolesById = _.keyBy(roles, 'id')
                userRole = userRole.map((ur) => ({
                  ...ur,
                  role: rolesById[ur.roleId]
                }))
                if (!isRegistered)
                  this.unregisteredUsers[this.editUser.index].userLink = {
                    ...(this.unregisteredUsers[this.editUser.index]?.userLink ||
                      {}),
                    userRole
                  }
                else if (tabValue == 'registered')
                  this.registeredUsers[this.editUser.index].userLink = {
                    ...(this.registeredUsers[this.editUser.index]?.userLink ||
                      {}),
                    userRole
                  }
                else if (tabValue == 'deactivated')
                  this.deactivatedUsers[this.editUser.index].userLink = {
                    ...(this.deactivatedUsers[this.editUser.index]?.userLink ||
                      {}),
                    userRole
                  }
              }
            }
          }
        } catch (e) {
          this.errorEditingUser =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
        }
        this.isLoadingEditingUser = false
      }
    },
    async editingUsers(users, roles, institution, tabValue = 'registered') {
      this.errorEditingUsers = null
      this.isLoadingEditingUsers = true
      this.statusEditingUsers = null
      try {
        // Update Roles
        let payload = {
          where: {
            id: {
              in: _.flatMap(users, ({ userLink }) =>
                _.map(userLink.userRole, 'id')
              )
            }
          },
          data: { deleteTs: 'softDelete' }
        }
        const result = await userRoleApi.patch('', payload)
        const { error } = result.data.response
        this.statusEditingUsers = result.data.status
        if (error) this.errorEditingUsers = error.message
        else {
          payload = {
            data: _.flatMap(users, ({ userLink }) =>
              roles.map(({ id: roleId }) => ({ linkId: userLink.id, roleId }))
            )
          }
          const result = await userRoleApi.post('', payload)
          let { error, userRole } = result.data.response
          this.statusEditingUser = result.data.status
          if (error) this.errorEditingUser = error.message
          else if (
            _.isEmpty(userRole) ||
            !_.isArrayLikeObject(userRole) ||
            userRole.length != users.length * roles.length
          )
            this.errorEditingUser =
              'Incomplete role mapping: resulting set of roles does not match the expected total.'
          else {
            const rolesById = _.keyBy(roles, 'id')
            userRole = userRole.map((ur) => ({
              ...ur,
              role: rolesById[ur.roleId]
            }))
            const rolesByUser = _.groupBy(userRole, 'linkId')
            users.forEach(
              (u) => (u.userLink.userRole = rolesByUser[u.userLink.id] || [])
            )
          }
        }
        // Update Institution
        if (this.errorEditingUser == null) {
          const institutionId = institution?.id || null
          payload = {
            where: {
              id: {
                in: _.map(users, ({ userLink }) => userLink.id)
              }
            },
            data: { institutionId },
            select: {
              id: true,
              userId: true,
              institutionId: true
            }
          }
          let result = await linkApi.patch('', payload)
          let { error } = result.data.response
          this.statusEditingUsers = result.data.status
          if (error) this.errorEditingUsers = error.message
          else {
            if (institutionId != null) {
              result = await institutionApi.get('', {
                params: {
                  where: {
                    id: institutionId
                  }
                }
              })
              ;({ error, institution } = result.data.response)
              this.statusEditingUsers = result.data.status
              if (error) this.errorEditingUsers = error.message
              else institution = institution[0]
            }
            const isDeactivated =
              institutionId != null &&
              institution != null &&
              !_.isEmpty(institution) &&
              institution.deleteTs != null
            if (this.errorEditingUsers == null) {
              if (this.registeredUsers == null || !this.registeredUsers.length)
                await this.fetchRegisteredUsers(false)
              else {
                users.forEach((u) => (u.userLink.institution = institution))
                if (tabValue == 'registered') {
                  if (isDeactivated) {
                    _.map(users, 'index')
                      .sort((a, b) => b - a)
                      .forEach((i) => this.registeredUsers.splice(i, 1))
                    this.deactivatedUsers = [
                      ...(this.deactivatedUsers ?? []),
                      ...users
                    ]
                  } else
                    users.forEach((u) => (this.registeredUsers[u.index] = u))
                } else if (tabValue == 'deactivated') {
                  if (isDeactivated)
                    users.forEach((u) => (this.deactivatedUsers[u.index] = u))
                  else {
                    _.map(users, 'index')
                      .sort((a, b) => b - a)
                      .forEach((i) => this.deactivatedUsers.splice(i, 1))
                    this.registeredUsers = [
                      ...(this.registeredUsers ?? []),
                      ...users
                    ]
                  }
                }
              }
            }
          }
        }
      } catch (e) {
        this.errorEditingUsers =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingEditingUsers = false
    },
    setFetchUnregisteredUsersInterval(newValue) {
      if (newValue == null && this.fetchUnregisteredUsersInterval != null)
        clearInterval(this.fetchUnregisteredUsersInterval)
      this.fetchUnregisteredUsersInterval = newValue
    },
    setFetchRegisteredUsersInterval(newValue) {
      if (newValue == null && this.fetchRegisteredUsersInterval != null)
        clearInterval(this.fetchRegisteredUsersInterval)
      this.fetchRegisteredUsersInterval = newValue
    },
    setFetchArchivedUsersInterval(newValue) {
      if (newValue == null && this.fetchArchivedUsersInterval != null)
        clearInterval(this.fetchArchivedUsersInterval)
      this.fetchArchivedUsersInterval = newValue
    },
    setFetchAvailableRolesInterval(newValue) {
      if (newValue == null && this.fetchAvailableRolesInterval != null)
        clearInterval(this.fetchAvailableRolesInterval)
      this.fetchAvailableRolesInterval = newValue
    },
    setEditUser(newValue) {
      this.editUser = newValue
    },
    setAdminTabs(newValue) {
      this.adminTabs = newValue
    },
    setAdminTableData() {
      const {
        data,
        headers,
        itemsPerPage,
        value,
        filterKeys,
        customFilters,
        customSort
      } = this.getAdminTabs?.[this.adminTabIndex] || {}
      this.adminTableData = {
        data,
        headers,
        itemsPerPage,
        value,
        filterKeys,
        customFilters,
        customSort
      }
    },
    setItemsPerPage({ itemsPerPage, value }) {
      this.itemsPerPage[value] = itemsPerPage
    },
    cancelInvitationsSentResults(type) {
      if (['success', 'failure', 'skipped'].includes(type)) {
        if (this.invitations.sentResults?.[type] != null)
          this.invitations.sentResults[type] = null
        const remaining = this.invitations.sentResults
        if (
          !remaining?.success?.length &&
          !remaining?.failure?.length &&
          !remaining?.skipped?.length
        )
          this.toggleSendingInvitationEmails()
      }
    },
    cancelError(key) {
      switch (key) {
        case 'invites':
          this.errorInvites = null
          break
        case 'unregisteredUsers':
          this.errorUnregisteredUsers = null
          break
        case 'registeredUsers':
          this.errorRegisteredUsers = null
          break
        case 'archivedUsers':
          this.errorArchivedUsers = null
          break
        case 'archivingUsers':
          this.errorArchivingUsers = null
          break
        case 'editingUser':
          this.errorEditingUser = null
          break
        case 'editingUsers':
          this.errorEditingUsers = null
          break
        case 'restoringUsers':
          this.errorRestoringUsers = null
          break
        default:
          break
      }
    },

    // ── Learner admin actions ──────────────────────────────────────────────

    async fetchLearners(institutionId = null) {
      this.isLoadingLearners = true
      this.learners = null
      try {
        const result = await registeredApi.get('')
        let users = result.data?.response?.userData ?? []
        users = users.filter((u) =>
          u.userLink?.userRole?.some(({ role }) => role.code === 'LRN')
        )
        if (institutionId)
          users = users.filter(
            (u) => u.userLink?.institution?.id === institutionId
          )
        this.learners = users
      } catch {
        this.learners = []
      } finally {
        this.isLoadingLearners = false
      }
    },

    async sendOtp(userId, institutionId = null) {
      this.isSendingOtp = true
      this.otp = null
      this.error = null
      try {
        const result = await registeredApi.post(
          '',
          { userId, institutionId },
          { params: { action: 'sendOtp' } }
        )
        this.otp = result.data?.response?.code ?? result.data?.code
        this.hint = result.data?.response?.hint ?? result.data?.hint
      } catch (e) {
        this.error =
          e.response?.data?.response?.error?.message ??
          e.response?.data?.message ??
          'Failed to send code.'
      } finally {
        this.isSendingOtp = false
      }
    },

    reset() {
      this.otp = null
      this.hint = null
      this.error = null
      this.learners = null
    },

    // ── Username checking (registration) ──────────────────────────────────

    checkUsername(val) {
      clearTimeout(_usernameCheckTimer)
      this.usernameHint = ''
      this.usernameError = ''
      this.usernameSuggestions = []
      if (!val) return
      const trimmed = val.replace(/^\s+|\s+$/g, '')
      if (!/^[a-zA-Z0-9._-]{3,30}$/.test(trimmed)) {
        this.usernameError =
          'Username must be 3–30 characters: letters, numbers, dots, hyphens or underscores only.'
        return
      }
      this.isCheckingUsername = true
      _usernameCheckTimer = setTimeout(async () => {
        try {
          const result = await authApi.post(
            'learner',
            { username: val },
            { params: { action: 'check' } }
          )
          const data = result.data?.response ?? result.data
          if (data.exists) {
            this.usernameError = 'Registered user'
            this.usernameSuggestions = [
              `${val}${Math.floor(Math.random() * 90 + 10)}`,
              `${val}_${Math.floor(Math.random() * 90 + 10)}`,
              `${val}.${new Date().getFullYear().toString().slice(-2)}`
            ]
          } else {
            this.usernameHint = 'Username is available'
          }
        } catch {
          this.usernameHint = ''
        } finally {
          this.isCheckingUsername = false
        }
      }, 400)
    },

    // ── Web learner login flow ─────────────────────────────────────────────

    async startLearnerLogin(username) {
      this.loginLoading = true
      this.loginError = null
      try {
        const result = await authApi.post(
          'learner',
          { username },
          { params: { action: 'check' } }
        )
        const data = result.data?.response ?? result.data
        if (!data.exists)
          throw new Error('No learner account found for that username.')
        this.loginUsername = username
        this.loginContactHint = data.hint ?? ''
        this.loginPassword = !!data.passwordLogin
        this.loginMustChangePassword = !!data.mustChangePassword
        this.loginStep = data.passwordLogin ? 'password' : 'challenge'
      } catch (e) {
        this.loginError =
          e?.response?.data?.response?.error?.message ??
          e?.response?.data?.message ??
          e.message
      } finally {
        this.loginLoading = false
      }
    },

    async submitLearnerChallenge(emailPrefix) {
      this.loginLoading = true
      this.loginError = null
      try {
        const result = await authApi.post(
          'learner',
          { username: this.loginUsername, emailPart: emailPrefix },
          { params: { action: 'challenge' } }
        )
        const data = result.data?.response ?? result.data
        if (data.method === 'otp') {
          this.loginStep = 'otp'
        } else if (data.method === 'password') {
          this.loginMustChangePassword = !!data.mustChangePassword
          this.loginStep = 'password'
        } else if (data.method === 'direct') {
          const { useAuthentication } = await import('./auth')
          await useAuthentication().fetchSession()
        }
      } catch (e) {
        this.loginError =
          e?.response?.data?.response?.error?.message ??
          e?.response?.data?.message ??
          'Verification failed.'
      } finally {
        this.loginLoading = false
      }
    },

    async submitLearnerPassword(password) {
      this.loginLoading = true
      this.loginError = null
      try {
        const result = await authApi.post(
          'learner',
          { username: this.loginUsername, password },
          { params: { action: 'password' } }
        )
        const data = result.data?.response ?? result.data
        if (data.mustChangePassword) {
          this.loginChangeToken = data.changeToken
          this.loginStep = 'setPassword'
          return
        }
        const { useAuthentication } = await import('./auth')
        const authStore = useAuthentication()
        authStore.user = data.user
        authStore.role = data.role
        authStore.roles = data.roles
        authStore.institution = data.institution
        authStore.provider = data.user?.provider
        authStore.statusAuthentication = 200
        authStore.errorAuthentication = null
        authStore.router.push('/')
        authStore.fetchSession()
      } catch (e) {
        this.loginError =
          e?.response?.data?.response?.error?.message ??
          e?.response?.data?.message ??
          'Invalid username or password.'
      } finally {
        this.loginLoading = false
      }
    },

    async submitLearnerSetPassword(newPassword) {
      this.loginLoading = true
      this.loginError = null
      try {
        const result = await authApi.post(
          'learner',
          { changeToken: this.loginChangeToken, newPassword },
          { params: { action: 'setPassword' } }
        )
        const { useAuthentication } = await import('./auth')
        const authStore = useAuthentication()
        const data = result.data?.response ?? result.data
        authStore.user = data.user
        authStore.role = data.role
        authStore.roles = data.roles
        authStore.institution = data.institution
        authStore.provider = data.user?.provider
        authStore.statusAuthentication = 200
        authStore.errorAuthentication = null
        authStore.router.push('/')
        authStore.fetchSession()
      } catch (e) {
        this.loginError =
          e?.response?.data?.response?.error?.message ??
          e?.response?.data?.message ??
          'Could not save the new password.'
      } finally {
        this.loginLoading = false
      }
    },

    async submitLearnerOtp(code) {
      this.loginLoading = true
      this.loginError = null
      try {
        const result = await authApi.post(
          'learner',
          { username: this.loginUsername, code },
          { params: { action: 'verify' } }
        )
        const { useAuthentication } = await import('./auth')
        const authStore = useAuthentication()
        const data = result.data?.response ?? result.data
        authStore.user = data.user
        authStore.role = data.role
        authStore.roles = data.roles
        authStore.institution = data.institution
        authStore.provider = data.user?.provider
        authStore.statusAuthentication = 200
        authStore.errorAuthentication = null
        authStore.router.push('/')
        authStore.fetchSession()
      } catch (e) {
        this.loginError =
          e?.response?.data?.response?.error?.message ??
          e?.response?.data?.message ??
          'Invalid or expired code.'
      } finally {
        this.loginLoading = false
      }
    },

    async resendLearnerOtp(emailPrefix) {
      this.loginResending = true
      this.loginError = null
      try {
        await authApi.post(
          'learner',
          { username: this.loginUsername, emailPart: emailPrefix },
          { params: { action: 'challenge' } }
        )
      } catch (e) {
        this.loginError =
          e?.response?.data?.response?.error?.message ??
          e?.response?.data?.message ??
          'Could not resend.'
      } finally {
        this.loginResending = false
      }
    },

    resetLearnerLogin() {
      this.loginStep = 'username'
      this.loginUsername = ''
      this.loginContactHint = ''
      this.loginError = null
      this.loginLoading = false
      this.loginResending = false
      this.loginPassword = false
      this.loginMustChangePassword = false
      this.loginChangeToken = null
    }
  }
})
